import React, { useEffect, useState } from "react";
import { useAuthenticator } from "@aws-amplify/ui-react";
import { useNavigate, NavLink, useLocation } from "react-router-dom";
import { signUp, confirmSignUp, resendSignUpCode, signIn } from "aws-amplify/auth";
import { Box, FormControl, FormLabel, Input, Link, Stack, Typography, formLabelClasses, Button, Sheet } from "@mui/joy";
import { encryptEmail } from "../../utils";
import { createUser } from "../../graphql/mutations";
import { generateClient } from "aws-amplify/api";
import { useMutation } from "react-query";
import { Check, Close } from "@mui/icons-material";
import { ColorSchemeToggle } from "../ColorSchemeToggle/index";

interface FormElements extends HTMLFormControlsCollection {
    email: HTMLInputElement;
    firstName: HTMLInputElement;
    lastName: HTMLInputElement;
    password: HTMLInputElement;
    confirmPassword: HTMLInputElement;
}

interface ConfirmationFormElements extends HTMLFormControlsCollection {
    confirmationCode: HTMLInputElement;
}

interface SignUpFormElement extends HTMLFormElement {
    readonly elements: FormElements;
}

interface ConfirmationFormElement extends HTMLFormElement {
    readonly elements: ConfirmationFormElements;
}

export const SignUp = () => {
    const client = generateClient();
    const [codeSent, setCodeSent] = useState(false);
    const [email, setEmail] = useState("");
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    const navigate = useNavigate();
    const location = useLocation();

    const typeOfSignup = location.pathname.split("/").pop();

    const [password, setPassword] = useState("");
    const [confirmPassword, setConfirmPassword] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [passwordValidations, setPasswordValidations] = useState({
        length: false,
        number: false,
        symbol: false,
        capital: false,
        lowercase: false,
        match: false,
    });
    const [isFormValid, setIsFormValid] = useState(false);
    const [confirmPasswordStarted, setConfirmPasswordStarted] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [navigationLoading, setNavigationLoading] = useState(false);

    const params = new URLSearchParams(window.location.search);
    const redirectUrl = params.get("redirect");

    const validatePassword = (password: string, confirmPassword: string) => {
        const validations = {
            length: password.length >= 8,
            number: /\d/.test(password),
            symbol: /[!@#$%^&*(),.?":{}|<>]/.test(password),
            capital: /[A-Z]/.test(password),
            lowercase: /[a-z]/.test(password),
            match: password === confirmPassword,
        };
        setPasswordValidations(validations);
        setIsFormValid(
            validations.length &&
                validations.number &&
                validations.symbol &&
                validations.capital &&
                validations.lowercase &&
                validations.match &&
                email !== "" &&
                firstName !== "" &&
                lastName !== ""
        );
    };

    useEffect(() => {
        if (authStatus === "authenticated") {
            navigate("/dashboard");
        }
    }, [authStatus, navigate]);

    useEffect(() => {
        if (typeof typeOfSignup === "string" && typeOfSignup !== "signup") {
            setEmail(typeOfSignup);
        }

        resendSignUpCodeMutation(undefined, {
            onError: (error) => {
                console.error("Error resending code:", error);
            },
        });
    }, [typeOfSignup]);

    const { mutate: signUpMutation, isLoading: signUpLoading } = useMutation(async (formElements: FormElements) => {
        const { userId } = await signUp({
            username: formElements.email.value,
            password: formElements.confirmPassword.value,
            options: {
                userAttributes: {
                    given_name: formElements.firstName.value,
                    family_name: formElements.lastName.value,
                },
            },
        });
        await client.graphql({
            query: createUser,
            variables: {
                input: {
                    id: userId,
                    email: formElements.email.value,
                    first_name: formElements.firstName.value,
                    last_name: formElements.lastName.value,
                },
            },
            authMode: "iam",
        });
    });

    const { mutate: confirmSignUpMutation, isLoading: confirmSignUpLoading } = useMutation(
        async (formElements: ConfirmationFormElements) => {
            await confirmSignUp({
                username: email,
                confirmationCode: formElements.confirmationCode.value,
            });
        }
    );

    const { mutate: resendSignUpCodeMutation, isLoading: resendSignUpCodeLoading } = useMutation(async () => {
        await resendSignUpCode({
            username: email,
        });
    });

    const fadeInOutStyle = (visible: boolean) => ({
        height: visible ? "auto" : "0px",
        opacity: visible ? 1 : 0,
        overflow: "hidden",
        transition: "height 500ms, opacity 500ms",
    });

    const showConfirmation =
        codeSent || (typeof typeOfSignup === "string" && typeOfSignup !== "signup" && email !== "");

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                flexDirection: "column",
                background: (theme) =>
                    theme.palette.mode === "dark"
                        ? "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.08) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(66, 171, 155, 0.05) 0%, transparent 50%)"
                        : "radial-gradient(ellipse at top, rgba(66, 171, 155, 0.12) 0%, transparent 60%), radial-gradient(ellipse at bottom right, rgba(230, 242, 239, 0.8) 0%, transparent 50%)",
            }}
        >
            {/* Header */}
            <Sheet
                sx={{
                    px: 3,
                    py: 1.5,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    bgcolor: "transparent",
                }}
            >
                <Typography
                    level="h4"
                    component={NavLink}
                    to="/"
                    sx={{
                        fontWeight: 800,
                        textDecoration: "none",
                        background: "linear-gradient(135deg, #42AB9B 0%, #328D7F 100%)",
                        WebkitBackgroundClip: "text",
                        WebkitTextFillColor: "transparent",
                    }}
                >
                    🦩 Stork
                </Typography>
                <ColorSchemeToggle />
            </Sheet>

            {/* Main Content */}
            <Box
                sx={{
                    flex: 1,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                    py: 4,
                }}
            >
                <Box
                    sx={{
                        width: "100%",
                        maxWidth: 420,
                        animation: "fadeInUp 0.5s ease-out",
                        "@keyframes fadeInUp": {
                            from: { opacity: 0, transform: "translateY(20px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                    }}
                >
                    <Sheet
                        variant="outlined"
                        sx={{
                            p: 4,
                            borderRadius: "xl",
                            boxShadow: "lg",
                            bgcolor: "background.surface",
                            [`& .${formLabelClasses.asterisk}`]: {
                                visibility: "hidden",
                            },
                        }}
                    >
                        {showConfirmation ? (
                            <Stack gap={4}>
                                <Stack gap={1} sx={{ textAlign: "center" }}>
                                    <Typography component="h1" level="h2" sx={{ fontWeight: 700 }}>
                                        Check your email
                                    </Typography>
                                    <Typography level="body-md" textColor="text.secondary">
                                        We sent a verification code to {encryptEmail(email)}
                                    </Typography>
                                    <Typography level="body-sm" textColor="text.tertiary">
                                        Check your spam folder if you don't see it
                                    </Typography>
                                </Stack>

                                <form
                                    onSubmit={async (event: React.FormEvent<ConfirmationFormElement>) => {
                                        event.preventDefault();
                                        const formElements = event.currentTarget.elements;

                                        confirmSignUpMutation(formElements, {
                                            onSuccess: async () => {
                                                setNavigationLoading(true);
                                                if (confirmPassword !== "") {
                                                    const { isSignedIn } = await signIn({
                                                        username: email,
                                                        password: confirmPassword,
                                                    });

                                                    if (isSignedIn) {
                                                        const params = new URLSearchParams(window.location.search);
                                                        const redirectUrl2 = params.get("redirect");
                                                        setNavigationLoading(false);
                                                        navigate(redirectUrl2 || "/dashboard");
                                                    } else {
                                                        setNavigationLoading(false);
                                                        navigate(
                                                            redirectUrl
                                                                ? `/signin?redirect=${encodeURIComponent(redirectUrl)}`
                                                                : "/signin"
                                                        );
                                                    }
                                                } else {
                                                    setNavigationLoading(false);
                                                    navigate(
                                                        redirectUrl
                                                            ? `/signin?redirect=${encodeURIComponent(redirectUrl)}`
                                                            : "/signin"
                                                    );
                                                }
                                            },
                                            onError: (error: any) => {
                                                console.error("Error signing in:", error);
                                                setNavigationLoading(false);
                                                const errorMessage = error?.message?.split(":")?.pop()?.trim() || "";
                                                setErrorMessage(errorMessage);
                                            },
                                        });
                                    }}
                                    style={{ display: "flex", flexDirection: "column", gap: "16px" }}
                                >
                                    <FormControl
                                        required
                                        error={errorMessage.startsWith("Invalid verification code provided")}
                                    >
                                        <FormLabel>Verification Code</FormLabel>
                                        <Input type="number" name="confirmationCode" size="lg" placeholder="123456" />
                                    </FormControl>

                                    {errorMessage && (
                                        <Typography level="body-sm" color="danger" sx={fadeInOutStyle(!!errorMessage)}>
                                            {errorMessage}
                                        </Typography>
                                    )}

                                    <Button type="submit" size="lg" loading={confirmSignUpLoading || navigationLoading}>
                                        Verify Email
                                    </Button>

                                    <Button
                                        type="button"
                                        variant="plain"
                                        color="neutral"
                                        loading={resendSignUpCodeLoading}
                                        onClick={async () => {
                                            resendSignUpCodeMutation(undefined, {
                                                onError: (error) => {
                                                    console.error("Error resending code:", error);
                                                },
                                            });
                                        }}
                                    >
                                        Didn't receive a code? Resend
                                    </Button>
                                </form>
                            </Stack>
                        ) : (
                            <Stack gap={3}>
                                <Stack gap={1} sx={{ textAlign: "center" }}>
                                    <Typography component="h1" level="h2" sx={{ fontWeight: 700 }}>
                                        Create your account
                                    </Typography>
                                    <Typography level="body-md" textColor="text.secondary">
                                        Start sharing your journey with loved ones
                                    </Typography>
                                </Stack>

                                <form
                                    onSubmit={async (event: React.FormEvent<SignUpFormElement>) => {
                                        event.preventDefault();
                                        const formElements = event.currentTarget.elements;
                                        setEmail(formElements.email.value);

                                        signUpMutation(formElements, {
                                            onSuccess: () => {
                                                setCodeSent(true);
                                            },
                                            onError: (error: any) => {
                                                const errorMessage = error?.message?.split(":")?.pop()?.trim() || "";
                                                setErrorMessage(errorMessage);
                                            },
                                        });
                                    }}
                                    style={{ display: "flex", flexDirection: "column", gap: "12px" }}
                                >
                                    <FormControl required error={errorMessage === "User already exists"}>
                                        <FormLabel>Email</FormLabel>
                                        <Input
                                            type="email"
                                            name="email"
                                            size="lg"
                                            value={email}
                                            onChange={(e) => {
                                                setEmail(e.target.value);
                                                validatePassword(password, confirmPassword);
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl required sx={{ flex: 1 }}>
                                        <FormLabel>First Name</FormLabel>
                                        <Input
                                            type="text"
                                            name="firstName"
                                            size="lg"
                                            value={firstName}
                                            onChange={(e) => {
                                                setFirstName(e.target.value);
                                                validatePassword(password, confirmPassword);
                                            }}
                                        />
                                    </FormControl>
                                    <FormControl required sx={{ flex: 1 }}>
                                        <FormLabel>Last Name</FormLabel>
                                        <Input
                                            type="text"
                                            name="lastName"
                                            size="lg"
                                            value={lastName}
                                            onChange={(e) => {
                                                setLastName(e.target.value);
                                                validatePassword(password, confirmPassword);
                                            }}
                                        />
                                    </FormControl>

                                    <FormControl required>
                                        <FormLabel>Password</FormLabel>
                                        <Input
                                            type="password"
                                            name="password"
                                            size="lg"
                                            value={password}
                                            onChange={(e) => {
                                                setPassword(e.target.value);
                                                validatePassword(e.target.value, confirmPassword);
                                            }}
                                        />
                                        <Stack
                                            gap={0.5}
                                            sx={{
                                                mt: password !== "" ? 1.5 : 0,
                                                ...fadeInOutStyle(password !== ""),
                                            }}
                                        >
                                            <Typography
                                                level="body-xs"
                                                color={passwordValidations.length ? "success" : "danger"}
                                                startDecorator={
                                                    passwordValidations.length ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                At least 8 characters
                                            </Typography>
                                            <Typography
                                                level="body-xs"
                                                color={passwordValidations.capital ? "success" : "danger"}
                                                startDecorator={
                                                    passwordValidations.capital ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                Contains a capital letter
                                            </Typography>
                                            <Typography
                                                level="body-xs"
                                                color={passwordValidations.lowercase ? "success" : "danger"}
                                                startDecorator={
                                                    passwordValidations.lowercase ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                Contains a lowercase letter
                                            </Typography>
                                            <Typography
                                                level="body-xs"
                                                color={passwordValidations.number ? "success" : "danger"}
                                                startDecorator={
                                                    passwordValidations.number ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                Contains a number
                                            </Typography>
                                            <Typography
                                                level="body-xs"
                                                color={passwordValidations.symbol ? "success" : "danger"}
                                                startDecorator={
                                                    passwordValidations.symbol ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                            >
                                                Contains a symbol
                                            </Typography>
                                        </Stack>
                                    </FormControl>

                                    <FormControl required>
                                        <FormLabel>Confirm Password</FormLabel>
                                        <Input
                                            type="password"
                                            name="confirmPassword"
                                            size="lg"
                                            value={confirmPassword}
                                            onChange={(e) => {
                                                setConfirmPassword(e.target.value);
                                                setConfirmPasswordStarted(true);
                                                validatePassword(password, e.target.value);
                                            }}
                                        />
                                        <Stack
                                            gap={0.5}
                                            sx={{
                                                mt: confirmPasswordStarted ? 1.5 : 0,
                                                ...fadeInOutStyle(confirmPasswordStarted),
                                            }}
                                        >
                                            <Typography
                                                startDecorator={
                                                    passwordValidations.match ? (
                                                        <Check sx={{ fontSize: 16 }} />
                                                    ) : (
                                                        <Close sx={{ fontSize: 16 }} />
                                                    )
                                                }
                                                level="body-xs"
                                                color={passwordValidations.match ? "success" : "danger"}
                                            >
                                                Passwords match
                                            </Typography>
                                        </Stack>
                                    </FormControl>

                                    {errorMessage && (
                                        <Typography level="body-sm" color="danger" sx={fadeInOutStyle(!!errorMessage)}>
                                            {errorMessage}
                                        </Typography>
                                    )}

                                    <Button
                                        type="submit"
                                        size="lg"
                                        loading={signUpLoading}
                                        disabled={!isFormValid}
                                        sx={{ mt: 1 }}
                                    >
                                        Create Account
                                    </Button>
                                </form>

                                <Typography level="body-sm" sx={{ textAlign: "center" }}>
                                    Already have an account?{" "}
                                    <Link
                                        component={NavLink}
                                        to={
                                            redirectUrl
                                                ? `/signin?redirect=${encodeURIComponent(redirectUrl)}`
                                                : "/signin"
                                        }
                                        fontWeight="lg"
                                    >
                                        Sign in
                                    </Link>
                                </Typography>
                            </Stack>
                        )}
                    </Sheet>
                </Box>
            </Box>
        </Box>
    );
};
